// =============================================================================
// Actions queued by taps inside the iOS home-screen widgets and by Siri
// (App Shortcuts).
//
// A widget cannot reach the app's data (it is a separate process with no
// session), so when someone ticks off a to-do or stops the timer there, the
// widget patches its own display and queues an *outcome* — "mark this done",
// "stop the running timer", "add Milch to the shopping list" — in the App
// Group. (Normally the widget writes straight to the server with the
// mirrored session; the queue is for when that failed.) The next time the app comes
// to the foreground it drains that queue (via the EuleaWidgetBridge plugin)
// and applies each action through the normal data path, so the change syncs
// to every device exactly like one made in the app.
//
// Actions are outcomes, never toggles, so applying one twice is harmless.
// =============================================================================

import { Capacitor, registerPlugin } from '@capacitor/core';

const Bridge = Capacitor.isNativePlatform() ? registerPlugin('EuleaWidgetBridge') : null;

// Everything the widgets queued since the last drain; empty on the web.
export const drainWidgetActions = async () => {
  if (!Bridge) return [];
  try {
    const res = await Bridge.drainActions();
    return Array.isArray(res?.actions) ? res.actions : [];
  } catch {
    return [];
  }
};

const newEntryId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// The widget may mint the id itself (so its own display and the row agree);
// otherwise one is made here.
const idFrom = (a) => (a.payload && a.payload.id != null ? String(a.payload.id) : newEntryId());

// Apply queued actions to `data`, returning the sections that changed in the
// shape `updateData` expects — or null when nothing applies (unknown ids,
// already done, no timer running). Pure: does not touch `data`.
export const applyWidgetActions = (data, actions) => {
  if (!data || !Array.isArray(actions) || actions.length === 0) return null;

  let tasks = data.todo?.tasks || [];
  let sharedTasks = data.todo?.sharedTasks || [];
  let items = data.shopping?.items || [];
  let entries = data.timetracker?.entries || [];
  let touched = { todo: false, shopping: false, timetracker: false };

  for (const a of actions) {
    if (!a || typeof a.type !== 'string') continue;
    const at = a.at || new Date().toISOString();
    const id = a.targetId != null ? String(a.targetId) : null;

    switch (a.type) {
      case 'todo.done': {
        const mark = (t) =>
          String(t.id) === id && !t.done ? { ...t, done: true, completedAt: at } : t;
        const nt = tasks.map(mark);
        const ns = sharedTasks.map(mark);
        if (nt !== tasks && nt.some((t, i) => t !== tasks[i])) { tasks = nt; touched.todo = true; }
        if (ns.some((t, i) => t !== sharedTasks[i])) { sharedTasks = ns; touched.todo = true; }
        break;
      }
      case 'shopping.check': {
        const ni = items.map((it) =>
          String(it.id) === id && !it.checked
            ? { ...it, checked: true, checkedAt: Date.parse(at) || Date.now() }
            : it
        );
        if (ni.some((it, i) => it !== items[i])) { items = ni; touched.shopping = true; }
        break;
      }
      case 'timer.stop': {
        const ne = entries.map((e) =>
          e && e.startedAt && !e.endedAt ? { ...e, endedAt: at } : e
        );
        if (ne.some((e, i) => e !== entries[i])) { entries = ne; touched.timetracker = true; }
        break;
      }
      case 'timer.start': {
        const running = entries.some((e) => e && e.startedAt && !e.endedAt);
        if (!running) {
          entries = [
            { id: idFrom(a), companyId: null, startedAt: at, endedAt: null, title: '', notes: '' },
            ...entries
          ];
          touched.timetracker = true;
        }
        break;
      }
      // A new item on the personal shopping list, shaped like ShoppingList.js's
      // addItem; skipped when the row already arrived through the server.
      case 'shopping.add': {
        const name = String(a.payload?.name || '').trim();
        const id = idFrom(a);
        if (!name || items.some((it) => String(it.id) === id)) break;
        const open = items.filter((it) => !it.sharedListId && !it.recipeId && !it.checked);
        items = [
          ...items,
          { id, name, amount: '', unit: '', recipeId: null, checked: false, sharedListId: null, position: open.length, createdAt: at }
        ];
        touched.shopping = true;
        break;
      }
      // A new personal to-do, shaped like Todo.js's saveDraft (newest first).
      case 'todo.add': {
        const title = String(a.payload?.title || '').trim();
        const id = idFrom(a);
        if (!title || tasks.some((t) => String(t.id) === id)) break;
        tasks = [
          { id, title, done: false, createdAt: at, date: null, time: null, priority: null, note: null, groupId: null },
          ...tasks
        ];
        touched.todo = true;
        break;
      }
      default:
        break;
    }
  }

  const updates = {};
  if (touched.todo) updates.todo = { ...(data.todo || {}), tasks, sharedTasks };
  if (touched.shopping) updates.shopping = { ...(data.shopping || {}), items };
  if (touched.timetracker) updates.timetracker = { ...(data.timetracker || {}), entries };
  return Object.keys(updates).length ? updates : null;
};
